import { loginAnonymously } from "@/api/auth/firebase"
import { useRegisterPlayer } from "@/api/auth/hooks"
import AppLoader from "@/components/shared/AppLoader"
import { useRunOnceReady } from "@/hooks/useRunOnceReady"
import * as Sentry from "@sentry/nextjs"
import { User, getAuth } from "firebase/auth"
import { PropsWithChildren, useRef, useState } from "react"
import AuthProvider from "./AuthProvider"

const Auth = (props: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null)

  const [loading, setLoading] = useState(true)
  const { mutateAsync: registerWithBE } = useRegisterPlayer()
  // Set while an anonymous session is being created and registered on the BE.
  const registering = useRef(false)

  useRunOnceReady(true, () => {
    const unsubscribe = getAuth().onAuthStateChanged(async function (user) {
      // signInAnonymously re-fires this listener before the BE sign-up is done;
      // the anonymous branch below publishes the user itself once registered.
      if (registering.current) return

      if (user) {
        setLoading(false)
        setUser(user)
        return
      }

      setUser(null)
      setLoading(true)
      registering.current = true

      try {
        const { data, error } = await loginAnonymously()

        if (!data) {
          Sentry.captureMessage(`Anonymous sign-in failed: ${error}`)
          return
        }

        // BE returns 401 for a player it does not know yet, so children (and
        // their queries) must not render before sign-up completes.
        try {
          await registerWithBE({})
        } catch (e) {
          Sentry.captureException(e)
        }

        setLoading(false)
        setUser(data.user)
      } finally {
        registering.current = false
      }
    })
    return () => {
      unsubscribe()
    }
  })

  if (loading || !user) {
    return <AppLoader className="w-full max-w-none bg-primary-700" />
  }

  return (
    <AuthProvider setUser={setUser} user={user}>
      {props.children}
    </AuthProvider>
  )
}

export default Auth
